import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchCompanySettings,
  setCompanySettings,
  fetchCompanyUsersThunk,
  inviteUserThunk,
  deleteInvitationThunk,
  fetchCompaniesList,
  fetchCompanyById,
  createCompany,
  editCompanyById,
  updateUserRoleThunk,
  resendInvitationThunk,
} from "../thunks/companyThunk.tsx";
import {
  CompanyState,
  CompanyUsersResponse,
  CompanySettingsType,
  CompaniesDataType,
  CompanyByIdItemDataType,
  CompanyItemDataType,
} from "../types/companyTypes.tsx";
import { formatErrorMessage } from "../../helpers/functions/formatErrorMessage.ts";

const initialState: CompanyState = {
  isLoading: false,
  isLoadingSettings: false,
  isLoadingCompaniesList: false,
  isLoadingUsers: false,
  isLoadingCompanyName: false,
  isLoadingCompanyById: false,
  isInviting: false,
  isDeletingInvitation: false,
  isUpdatingRole: false,
  isResendingInvitation: false,
  companiesList: null,
  companySettings: null,
  companyUsers: null,
  companyById: null,
  errorEditCompany: null,
  errorCreateCompany: null,
  error: null,
};

export const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = initialState.error;
    },
    clearErrorEditCompany: (state) => {
      state.errorEditCompany = initialState.errorEditCompany;
    },
    clearErrorCreateCompany: (state) => {
      state.errorCreateCompany = initialState.errorCreateCompany;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCompanySettings.pending, (state) => {
      state.isLoadingSettings = true;
    });
    builder.addCase(
      fetchCompanySettings.fulfilled,
      (state, action: PayloadAction<CompanySettingsType>) => {
        state.isLoadingSettings = false;
        state.companySettings = action.payload;
      },
    );
    builder.addCase(fetchCompanySettings.rejected, (state, action) => {
      state.isLoadingSettings = false;
      state.error = action.payload as string;
    });
    builder.addCase(fetchCompaniesList.pending, (state) => {
      state.isLoadingCompaniesList = true;
    });
    builder.addCase(
      fetchCompaniesList.fulfilled,
      (state, action: PayloadAction<CompaniesDataType>) => {
        state.isLoadingCompaniesList = false;
        state.companiesList = action.payload;
      },
    );
    builder.addCase(fetchCompaniesList.rejected, (state, action) => {
      state.isLoadingCompaniesList = false;
      state.error = action.payload as string;
    });
    builder.addCase(fetchCompanyById.pending, (state) => {
      state.isLoadingCompanyById = true;
    });
    builder.addCase(
      fetchCompanyById.fulfilled,
      (state, action: PayloadAction<CompanyByIdItemDataType>) => {
        state.isLoadingCompanyById = false;
        state.companyById = action.payload;
      },
    );
    builder.addCase(fetchCompanyById.rejected, (state, action) => {
      state.isLoadingCompanyById = false;
      state.error = action.payload as string;
    });
    builder.addCase(editCompanyById.pending, (state) => {
      state.isLoadingCompanyName = true;
    });
    builder.addCase(
      editCompanyById.fulfilled,
      (state, action: PayloadAction<CompanyItemDataType>) => {
        state.isLoadingCompanyName = false;
        if (state.companyById) {
          state.companyById = {
            ...state.companyById,
            name: action.payload.name,
          };
        }
        if (state.companiesList && state.companiesList.length > 0) {
          state.companiesList = state.companiesList.map((company) =>
            company.id.toString() === action.payload.id.toString()
              ? { ...company, name: action.payload.name }
              : company,
          );
        }
        state.errorEditCompany = initialState.errorEditCompany;
      },
    );
    builder.addCase(editCompanyById.rejected, (state, action) => {
      state.isLoadingCompanyName = false;
      state.errorEditCompany = formatErrorMessage(action.payload as string);
    });

    builder.addCase(
      createCompany.fulfilled,
      (state, action: PayloadAction<CompanyItemDataType>) => {
        if (action.payload) {
          if (state.companiesList && state.companiesList.length > 0) {
            state.companiesList = [...state.companiesList, action.payload];
          } else {
            state.companiesList = [action.payload];
          }
        }
      },
    );
    builder.addCase(createCompany.rejected, (state, action) => {
      state.errorCreateCompany = formatErrorMessage(action.payload as string);
    });
    builder.addCase(setCompanySettings.pending, (state) => {
      state.isLoadingCompanyName = true;
    });
    builder.addCase(
      setCompanySettings.fulfilled,
      (state, action: PayloadAction<CompanySettingsType>) => {
        state.isLoadingCompanyName = false;
        if (action.payload) {
          state.companySettings = action.payload;
        }
      },
    );
    builder.addCase(setCompanySettings.rejected, (state, action) => {
      state.isLoadingCompanyName = false;
      state.errorEditCompany = formatErrorMessage(action.payload as string);
    });

    builder.addCase(fetchCompanyUsersThunk.pending, (state) => {
      state.isLoadingUsers = true;
      state.error = null;
    });
    builder.addCase(
      fetchCompanyUsersThunk.fulfilled,
      (state, action: PayloadAction<CompanyUsersResponse>) => {
        state.isLoadingUsers = false;
        state.companyUsers = action.payload;
      },
    );
    builder.addCase(deleteInvitationThunk.pending, (state) => {
      state.isDeletingInvitation = true;
    });
    builder.addCase(
      deleteInvitationThunk.fulfilled,
      (state, { payload: deletedInvitationId }: PayloadAction<number>) => {
        state.isDeletingInvitation = false;
        if (!state.companyUsers?.pending_invitations) {
          return;
        }
        state.companyUsers.pending_invitations =
          state.companyUsers.pending_invitations.filter(
            (inv) => inv.id !== deletedInvitationId,
          );
      },
    );
    builder.addCase(deleteInvitationThunk.rejected, (state) => {
      state.isDeletingInvitation = false;
    });
    builder.addCase(fetchCompanyUsersThunk.rejected, (state, action) => {
      state.isLoadingUsers = false;
      state.error = action.payload as string;
    });
    builder.addCase(inviteUserThunk.pending, (state) => {
      state.isInviting = true;
    });
    builder.addCase(inviteUserThunk.fulfilled, (state) => {
      state.isInviting = false;
    });
    builder.addCase(inviteUserThunk.rejected, (state) => {
      state.isInviting = false;
    });
    builder.addCase(updateUserRoleThunk.pending, (state) => {
      state.isUpdatingRole = true;
    });
    builder.addCase(updateUserRoleThunk.fulfilled, (state) => {
      state.isUpdatingRole = false;
    });
    builder.addCase(updateUserRoleThunk.rejected, (state) => {
      state.isUpdatingRole = false;
    });
    builder.addCase(resendInvitationThunk.pending, (state) => {
      state.isResendingInvitation = true;
    });
    builder.addCase(resendInvitationThunk.fulfilled, (state) => {
      state.isResendingInvitation = false;
    });
    builder.addCase(resendInvitationThunk.rejected, (state) => {
      state.isResendingInvitation = false;
    });
  },
});

export const { clearError, clearErrorEditCompany } = companySlice.actions;
